import { useEffect, useMemo } from "react";
import * as Yup from "yup";
import { ContactFormSchema, contactFormInitialValues, IContactForm, onSubmitContactForm } from "@/pages/reservations/components/ContactForm";
import { DateFormSchema, dateFormInitialValues, IDateForm } from "@/pages/reservations/components/DateForm";
import { TimeFormSchema, timeFormInitialValues, ITimeForm } from "@/pages/reservations/components/TimeForm";
import { CheckoutSharedProps } from "@/pages/checkout/CheckoutPage";
import useRoutes from "@/hooks/useRoutes";

export type IReservation = IContactForm & IDateForm & ITimeForm;

export interface Step {
  id: string;
  title: string;
  schema?: Yup.AnyObject;
  initialValues?: IContactForm | IDateForm | ITimeForm;
  onSubmit?: (values: IReservation, sharedProps: CheckoutSharedProps) => void;
}

const CONTACT_STEP: Step = {
  id: "contact",
  title: "Contacto",
  schema: ContactFormSchema,
  initialValues: contactFormInitialValues,
  onSubmit: onSubmitContactForm,
};

const DATE_STEP: Step = {
  id: "date",
  title: "Fecha",
  schema: DateFormSchema,
  initialValues: dateFormInitialValues,
};

const TIME_STEP: Step = {
  id: "time",
  title: "Horario",
  schema: TimeFormSchema,
  initialValues: timeFormInitialValues,
};

const CONFIRMATION_STEP: Step = {
  id: "confirmation",
  title: "Confirmación",
};

const STEPS = [CONTACT_STEP, DATE_STEP, TIME_STEP, CONFIRMATION_STEP];

const useSteps = () => {
  const [navigate] = useRoutes();

  const stepId = location.pathname.split("/").pop();

  useEffect(() => {
    if (stepId === "reservations" || stepId === "") {
      navigate("organizations.reservations.form", { formId: "contact" }, { replace: true });
    }
  }, [stepId]);

  const activeStep = useMemo(() => {
    const step = STEPS.find((step) => step.id === stepId);
    return step ?? STEPS[0];
  }, [stepId]);

  const isFirstStep = useMemo(() => {
    const index = STEPS.findIndex((step) => step.id === stepId);
    return index === 0;
  }, [stepId]);

  const isLastStep = useMemo(() => {
    const index = STEPS.findIndex((step) => step.id === stepId);
    return index === STEPS.length - 1;
  }, [stepId]);

  const previousStep = useMemo(() => {
    const index = STEPS.findIndex((step) => step.id === stepId);
    if (index === 0) return STEPS[0];
    return STEPS[index - 1];
  }, [stepId]);

  const nextStep = useMemo(() => {
    const index = STEPS.findIndex((step) => step.id === stepId);
    if (index === STEPS.length - 1) return STEPS[STEPS.length - 1];
    return STEPS[index + 1];
  }, [stepId]);

  const initialValues = useMemo(() => {
    if (!STEPS.length) return {};

    return STEPS.reduce(
      (acc, curr) => ({
        ...acc,
        ...(curr?.initialValues ?? {}),
      }),
      {} as IReservation
    );
  }, []);

  const validationSchema = useMemo(() => {
    if (isLastStep) {
      const draft = [...STEPS];
      draft.pop();

      return draft.reduce((acc, curr) => acc.concat(curr.schema), Yup.object().shape({}));
    }

    const step = STEPS.find((step) => step.id === stepId);
    return step?.schema;
  }, [isLastStep, stepId]);

  return {
    steps: STEPS,
    stepId,
    activeStep,
    isFirstStep,
    isLastStep,
    previousStep,
    nextStep,
    initialValues,
    validationSchema,
  };
};

export default useSteps;
